import { type FormEvent } from 'react';
import { Banner, Button, Input } from '@servicecompressionllc/sc-design-system';
import { MarkdownViewer } from '../../components/MarkdownViewer.js';
import type { AssistantPolicy } from '../../lib/types.js';
import { CrumbHeader } from './CrumbHeader.js';
import { useSaveDraftLogic } from './useGuardrailsLogic.js';
import { documentFileName, downloadTextFile, formatBytes, type UploadDraft } from './shared.js';

/**
 * The formatted draft, before anything is enforced. Deliberately quiet — one
 * status line and one "left out" line rather than stacked alerts, because the
 * thing the admin needs to read is the draft itself.
 */
export const ReviewUploadView = ({
  tenantId,
  draft,
  initialName,
  onSaved,
  onSavedAddRules,
  onDiscard,
}: {
  tenantId: string;
  draft: UploadDraft;
  initialName: string;
  onSaved: () => void;
  /** Save, then chain into the add-rules workflow targeting the saved document. */
  onSavedAddRules: (policy: AssistantPolicy) => void;
  onDiscard: () => void;
}) => {
  const logic = useSaveDraftLogic(tenantId, draft, initialName);

  const saveThen = async (next: (policy: AssistantPolicy) => void) => {
    if (!logic.canSubmit) return;
    try {
      next(await logic.save());
    } catch {
      // Surfaced via logic.error.
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await saveThen(() => onSaved());
  };

  return (
    <>
      <CrumbHeader title="Review upload" onBack={onDiscard} />
      <div>
        <h2 className="text-ink mb-1.5 text-2xl font-bold">Review upload</h2>
        <p className="text-ink-muted mb-4 text-sm">
          {draft.filename} · {formatBytes(draft.size)} · formatted into a constraint catalog —
          review before saving
        </p>

        {logic.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}

        <p className="text-ink-muted mb-1.5 text-sm">
          <b className="text-success font-semibold">
            {draft.rulesCount} {draft.rulesCount === 1 ? 'rule' : 'rules'} formatted
          </b>{' '}
          from your document — wording preserved where possible. Review below; nothing is enforced
          until you save.
        </p>
        {draft.leftOut.length > 0 && (
          <p className="text-ink-subtle mb-4 text-xs">
            Left out (not rules): {draft.leftOut.join(' · ')}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mt-4 w-full max-w-[300px]">
            <label htmlFor="draft-name" className="text-ink mb-1.5 block text-xs font-semibold">
              Document name
            </label>
            <Input
              id="draft-name"
              type="text"
              value={logic.name}
              onChange={(e) => logic.setName(e.target.value)}
              disabled={logic.isSubmitting}
              autoComplete="off"
            />
          </div>

          <div className="border-outline bg-surface-elevated mb-4 mt-4 max-h-[60vh] overflow-y-auto rounded-xl border px-6 py-5">
            <MarkdownViewer variant="policy">{draft.document}</MarkdownViewer>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                downloadTextFile(documentFileName(logic.name || draft.filename), draft.document)
              }
              disabled={logic.isSubmitting}
            >
              Download draft
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={onDiscard}
              disabled={logic.isSubmitting}
            >
              Discard
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void saveThen(onSavedAddRules)}
              disabled={!logic.canSubmit}
            >
              Save & add rules
            </Button>
            <Button type="submit" disabled={!logic.canSubmit}>
              {logic.isSubmitting ? 'Saving…' : 'Save as document'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
